import "./EssayProgressOverlay.scss";

import { isEqual } from "lodash";
import React, { useMemo, useState } from "react";

import { useRect } from "@reach/rect";

import {
  essayProgressIndicatorNodeHeight,
  essayProgressIndicatorNodeWidth,
  getEssayProgressIndicatorNodeLocations,
} from "./EssayProgressIndicator";
import { SpacedRepetitionSchedule } from "../spacedRepetition";

export type EssayProgressOverlayChildFactory = (
  nodeLocations: number[][],
  nodeWidth: number,
  nodeHeight: number,
) => React.ReactElement;

interface Props {
  targetElement: HTMLElement;
  scaleFactor?: number;
  schedule: SpacedRepetitionSchedule;
  children: EssayProgressOverlayChildFactory;
}

export default React.memo(
  React.forwardRef(function EssayProgressOverlay<T>(
    props: Props,
    ref: React.Ref<HTMLDivElement>,
  ) {
    const { targetElement, schedule, children } = props;
    const scaleFactor = props.scaleFactor || 1.0;

    const targetProxyRef = useMemo(() => ({ current: targetElement }), [
      targetElement,
    ]);
    const targetViewportPosition = useRect(targetProxyRef) as DOMRect | null;
    const [
      containerElement,
      setContainerElement,
    ] = useState<HTMLDivElement | null>(null);

    let containerPosition: {
      left: number;
      top: number;
    } | null = targetViewportPosition;
    // Special-casing when the target and the container have the same offset parent--I don't really want to do a full coordinate system conversion.
    if (
      targetViewportPosition &&
      containerElement &&
      containerElement.offsetParent === targetElement.offsetParent
    ) {
      containerPosition = {
        left: targetElement.offsetLeft,
        top: targetElement.offsetTop,
      };
    }

    const {
      exponential: exponentialNodeLocations,
    } = getEssayProgressIndicatorNodeLocations(schedule);
    const nodeLocations =
      scaleFactor === undefined
        ? exponentialNodeLocations
        : exponentialNodeLocations.map(n => [
            n[0] * scaleFactor,
            (n[1] + 47) * scaleFactor,
          ]);
    // 47 matches padding in EssayProgressIndicator.scss
    const nodeWidth =
      scaleFactor === undefined
        ? essayProgressIndicatorNodeWidth
        : essayProgressIndicatorNodeWidth * scaleFactor;
    const nodeHeight =
      scaleFactor === undefined
        ? essayProgressIndicatorNodeHeight
        : essayProgressIndicatorNodeHeight * scaleFactor;
    return (
      <div
        className="EssayProgressOverlay-Callouts"
        style={{
          position:
            !containerElement ||
            containerElement.offsetParent === targetElement.offsetParent
              ? "absolute"
              : "fixed",
        }}
        ref={setContainerElement}
      >
        {containerElement && containerPosition && (
          <div
            style={{
              position: "absolute",
              left: containerPosition.left - 5,
              top: containerPosition.top, // matching offsets in EssayProgressIndicator
            }}
            ref={ref}
          >
            {children(nodeLocations, nodeWidth, nodeHeight)}
          </div>
        )}
      </div>
    );
  }),
  (a, b) => {
    const { targetElement: aTargetElement, ...aProps } = a;
    const { targetElement: bTargetElement, ...bProps } = b;
    return isEqual(aProps, bProps);
  },
);
